/**
 * KarnoffelRoom — main play surface for the 2-player Karnöffel table.
 *
 * Layout (top → bottom):
 *   • TopBar  +  NavPane on the left
 *   • Title / connection / score badge
 *   • Opponent hand (face-down, count only)
 *   • Trick area — trump indicator card + slot for led card / your card
 *   • Hand row (your cards, click to play)
 *   • Trump hierarchy reference (collapsible)
 *
 * Game rules summary lives in `backend/games/karnoffel_engine.py`.
 */
import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import { KARNOFFEL_CFG } from "@/components/MatchKey/cfgs";

const PAL = KARNOFFEL_CFG.palette;
const FONTS = KARNOFFEL_CFG.fonts;
const TIMES = "'Times New Roman', Times, serif";

const SUIT_GLYPH = { S: "\u2660", H: "\u2665", D: "\u2666", C: "\u2663" };
const SUIT_COLOR = { S: "#0F0E0C", C: "#0F0E0C", H: "#B33333", D: "#B33333" };

function Card({ c, faceDown, onClick, disabled, highlight }) {
  if (faceDown) {
    return (
      <div
        className="rounded-md"
        style={{
          width: 60, height: 86,
          background: "linear-gradient(135deg,#3A2A1B 0%,#5C3A1E 50%,#2C1B0F 100%)",
          border: "1px solid #C9A560",
          boxShadow: "0 2px 4px rgba(0,0,0,0.55)",
        }}
      />
    );
  }
  if (!c) return null;
  return (
    <button
      onClick={onClick}
      data-silent="true"
      data-testid={`karnoffel-card-${c.id}`}
      disabled={disabled}
      style={{
        width: 60, height: 86,
        background: disabled ? "#E5DEC8" : "#FFFDF6",
        color: SUIT_COLOR[c.s],
        opacity: disabled ? 0.55 : 1,
        border: highlight ? "2px solid #E5C870" : "1px solid #C4B083",
        borderRadius: 6,
        boxShadow: highlight
          ? "0 0 0 2px #E5C870, 0 4px 8px rgba(0,0,0,0.45)"
          : "0 2px 4px rgba(0,0,0,0.5)",
        fontFamily: TIMES, fontSize: 18, fontWeight: 700,
        padding: 6, display: "flex", flexDirection: "column", alignItems: "flex-start",
        cursor: disabled ? "default" : "pointer",
        transform: highlight ? "translateY(-6px)" : "translateY(0)",
        transition: "transform 0.15s ease-out, box-shadow 0.15s ease-out",
      }}
    >
      <span>{c.r}</span>
      <span style={{ fontSize: 24, alignSelf: "center", marginTop: 2 }}>{SUIT_GLYPH[c.s]}</span>
    </button>
  );
}

const TRUMP_NAMES_BY_RANK = {
  "2": "Devil",
  "7": "Karn\u00f6ffel",
  "J": "Pope",
  "6": "Kaiser",
};

function TrumpLadder({ trump }) {
  const rows = [
    { r: "2",  name: "Devil",     note: "beats everything" },
    { r: "7",  name: "Karn\u00f6ffel", note: "beats all except Devil" },
    { r: "J",  name: "Pope (Unter)", note: "beats all plain + lower trumps" },
    { r: "6",  name: "Kaiser",    note: "" },
    { r: "3",  name: "",          note: "" },
    { r: "5",  name: "",          note: "" },
    { r: "4",  name: "",          note: "" },
    { r: "K/Q/10/9/8", name: "low trumps", note: "still beat any plain card" },
  ];
  return (
    <div
      data-testid="karnoffel-trump-ladder"
      style={{
        background: "rgba(245,235,208,0.04)",
        border: `1px solid ${PAL.border}`,
        borderRadius: 4,
        padding: "12px 16px",
        fontFamily: FONTS.body,
        color: PAL.ink,
        fontSize: 16,
        maxWidth: 360,
      }}
    >
      <div style={{ fontFamily: FONTS.eyebrow, color: PAL.accent, letterSpacing: "0.1em", marginBottom: 6, fontSize: 16 }}>
        TRUMP LADDER {trump ? `\u2014 ${SUIT_GLYPH[trump]} ${trump}` : ""}
      </div>
      <table style={{ width: "100%", borderSpacing: 0, color: PAL.muted }}>
        <tbody>
          {rows.map((row) => (
            <tr key={row.r} style={{ verticalAlign: "top" }}>
              <td style={{ color: PAL.accent, fontWeight: 700, paddingRight: 8, whiteSpace: "nowrap" }}>{row.r}</td>
              <td style={{ color: PAL.ink, paddingRight: 8, whiteSpace: "nowrap" }}>{row.name}</td>
              <td style={{ color: PAL.muted, fontStyle: "italic", fontSize: 16 }}>{row.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function KarnoffelRoom() {
  const navigate = useNavigate();
  const { key } = useParams();
  const [params] = useSearchParams();
  const nameFromQs =
    params.get("name") ||
    (typeof localStorage !== "undefined" ? localStorage.getItem("karnoffel:name") : "") ||
    "Player";

  const [state, setState] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl =
      apiBase.replace(/^http/, "ws") +
      `/api/karnoffel/ws/${key}?name=${encodeURIComponent(nameFromQs)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "state") setState(msg);
      } catch { /* */ }
    };
    return () => { try { ws.close(); } catch { /* */ } };
  }, [key, nameFromQs]);

  const send = (obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  };

  // ── Derived gameplay info ─────────────────────────────────────────
  const phase = state?.phase || "waiting";
  const myRole = state?.your_role;
  const led = state?.led;
  const ledSuit = led?.card?.s;
  // Whose turn is it?
  // If led is null: the leader leads.
  // If led is set:  the follower (the other role) plays.
  const turnRole = useMemo(() => {
    if (!state || !state.leader) return null;
    if (!led) return state.leader;
    return state.leader === "host" ? "guest" : "host";
  }, [state, led]);
  const isMyTurn = phase === "play" && turnRole === myRole;

  // Filter hand to legal plays (must follow led suit if you can).
  const canPlay = (card) => {
    if (!isMyTurn) return false;
    if (!ledSuit) return true;
    const hasSuit = (state.your_hand || []).some((c) => c.s === ledSuit);
    if (!hasSuit) return true;
    return card.s === ledSuit;
  };

  const isFinished = phase === "finished";

  return (
    <div className="min-h-screen karnoffel-scope" style={{ background: PAL.bg, color: PAL.ink }} data-testid="karnoffel-room">
      <TopBar
        theme="studio"
        onBack={() => navigate("/games/karnoffel")}
        backLabel="LOBBY"
        testId="karnoffel-room-back-button"
      />
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div
          className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
          style={{ borderColor: "rgba(201,165,96,0.22)" }}
        >
          <NavPane theme="studio" />
        </div>
        <main className="flex-1 flex flex-col pt-10 px-6 md:px-10 pb-10">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
            <h1
              data-testid="karnoffel-room-title"
              style={{
                fontFamily: FONTS.title,
                fontWeight: FONTS.titleWeight,
                color: PAL.accent,
                fontSize: "clamp(2.2rem,4vw,3.6rem)",
                letterSpacing: FONTS.titleSpacing,
                textShadow: PAL.titleShadow,
              }}
            >
              Karn&ouml;ffel
            </h1>
            <div style={{ fontFamily: FONTS.body, color: PAL.muted, fontSize: 16 }}>
              Key{" "}
              <span data-testid="karnoffel-room-key" style={{ color: PAL.accent, letterSpacing: "0.18em" }}>
                {key}
              </span>
              <span
                data-testid="karnoffel-conn-status"
                style={{ marginLeft: 12, color: connected ? "#7CE38B" : "#E07070" }}
              >
                {connected ? "\u2022 connected" : "\u2022 reconnecting\u2026"}
              </span>
            </div>
          </div>

          {!state && (
            <p style={{ fontFamily: FONTS.body, color: PAL.muted }} data-testid="karnoffel-joining">
              Taking a seat at the table&hellip;
            </p>
          )}

          {state && (
            <>
              <div className="mb-4" style={{ fontFamily: FONTS.body, color: PAL.muted }} data-testid="karnoffel-roster">
                Host: <span style={{ color: PAL.ink }}>{state.host_name || "\u2026"}</span>
                {"   "}|{"   "}
                Guest: <span style={{ color: PAL.ink }}>{state.guest_name || "\u2026"}</span>
                {"   "}|{"   "}
                Match{" "}
                <span style={{ color: PAL.ink }}>
                  H {state.scores.host} &middot; G {state.scores.guest}
                </span>{" "}
                (first to {state.match_target})
              </div>

              {(!state.host_name || !state.guest_name) && (
                <p
                  style={{ fontFamily: FONTS.body, color: PAL.muted, marginBottom: "1rem" }}
                  data-testid="karnoffel-await-opponent"
                >
                  Waiting for the other player to enter the room key&hellip;
                </p>
              )}

              <div className="mb-3" style={{ fontFamily: FONTS.body, color: PAL.accent }} data-testid="karnoffel-status">
                {state.last_msg}
                {isMyTurn && phase === "play" ? " \u2014 your turn." : ""}
              </div>

              {/* Opponent hand (face-down) */}
              <div className="flex flex-wrap gap-2 mb-6" data-testid="karnoffel-opp-hand">
                {Array.from({ length: state.opp_hand_count }).map((_, i) => (
                  <Card key={i} faceDown />
                ))}
              </div>

              {/* Trick area: turn-up card (trump indicator) + led card + your-card slot */}
              <div className="flex flex-wrap items-end gap-6 mb-6" data-testid="karnoffel-trick-area">
                <div className="flex flex-col items-start">
                  <div
                    style={{
                      fontFamily: FONTS.eyebrow,
                      color: PAL.muted,
                      letterSpacing: "0.1em",
                      fontSize: 16,
                      marginBottom: 4,
                    }}
                  >
                    TRUMP
                  </div>
                  <Card c={state.turnup} />
                </div>
                <div className="flex flex-col items-start">
                  <div
                    style={{
                      fontFamily: FONTS.eyebrow,
                      color: PAL.muted,
                      letterSpacing: "0.1em",
                      fontSize: 16,
                      marginBottom: 4,
                    }}
                  >
                    LED
                  </div>
                  {led ? <Card c={led.card} highlight={ledSuit === state.trump} /> : <Card faceDown />}
                </div>
                <div className="flex flex-col items-start">
                  <div
                    style={{
                      fontFamily: FONTS.eyebrow,
                      color: PAL.muted,
                      letterSpacing: "0.1em",
                      fontSize: 16,
                      marginBottom: 4,
                    }}
                  >
                    YOU
                  </div>
                  {state.last_trick && state.last_trick.follow.role === myRole ? (
                    <Card c={state.last_trick.follow.card} />
                  ) : state.last_trick && state.last_trick.led.role === myRole && !led ? (
                    <Card c={state.last_trick.led.card} />
                  ) : (
                    <Card faceDown />
                  )}
                </div>
                <div className="flex flex-col items-start" style={{ marginLeft: "auto" }}>
                  <div
                    style={{
                      fontFamily: FONTS.eyebrow,
                      color: PAL.muted,
                      letterSpacing: "0.1em",
                      fontSize: 16,
                      marginBottom: 4,
                    }}
                  >
                    TRICKS — H {state.tricks.host} &middot; G {state.tricks.guest}
                  </div>
                  {state.last_trick && (
                    <div
                      style={{ fontFamily: FONTS.body, color: PAL.muted, fontSize: 16, fontStyle: "italic" }}
                    >
                      Last: {state.last_trick.winner === myRole ? "you won" : "opponent won"}
                    </div>
                  )}
                </div>
              </div>

              {/* Your hand */}
              <div className="mb-5" style={{ fontFamily: FONTS.eyebrow, color: PAL.muted, letterSpacing: "0.1em", fontSize: 16 }}>
                YOUR HAND
              </div>
              <div className="flex flex-wrap gap-3 mb-8" data-testid="karnoffel-my-hand">
                {(state.your_hand || []).map((c) => {
                  const isTrump = c.s === state.trump;
                  const tname = isTrump ? TRUMP_NAMES_BY_RANK[c.r] : null;
                  return (
                    <div key={c.id} className="flex flex-col items-center" style={{ minWidth: 60 }}>
                      <Card
                        c={c}
                        disabled={!canPlay(c)}
                        onClick={() => canPlay(c) && send({ type: "play", card_id: c.id })}
                      />
                      {tname && (
                        <span
                          style={{ marginTop: 2, fontFamily: FONTS.body, color: PAL.accent, fontSize: 16, fontStyle: "italic" }}
                        >
                          {tname}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {isFinished && (
                <div className="mb-6">
                  <button
                    data-testid="karnoffel-rematch-btn"
                    onClick={() => send({ type: "rematch" })}
                    style={{
                      fontFamily: FONTS.eyebrow,
                      letterSpacing: "0.12em",
                      padding: "0.6rem 1.4rem",
                      background: PAL.accent,
                      color: PAL.btnInk,
                      border: `1px solid ${PAL.border}`,
                      cursor: "pointer",
                    }}
                  >
                    REMATCH
                  </button>
                </div>
              )}

              <TrumpLadder trump={state.trump} />
            </>
          )}
        </main>
      </div>
    </div>
  );
}
